/**
 * Production adapter for rename / delete / duplicate / replace contours /
 * type: the SAME domain events the structure list publishes (via `useRoi`
 * and `useStructureSet`, with the active set's DICOM UID), handled by the
 * viewer's own handlers with their own checks:
 *  - rename `RoiNameChangeRequestedEvent`, type
 *    `RoiInterpretedTypeChangeRequestedEvent` — then, like the list's
 *    command path (`useRoiCommands`), the unsaved-changes guard is armed;
 *  - delete: clear the selection, then `RoiDeletionRequestedEvent` (the
 *    deletion dialog's confirm step);
 *  - duplicate `RoiCopyRequestedEvent`; replace `RoiSdfCopyOverRequestedEvent`
 *    (these handlers mark unsaved changes themselves).
 * Avoids the session-requiring command hooks (the drawer is mounted before
 * a patient is loaded). Browser glue covered by the manual demo; the logic
 * is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import type { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiCopyRequestedEvent,
    RoiDeletionRequestedEvent,
    RoiInterpretedTypeChangeRequestedEvent,
    RoiNameChangeRequestedEvent,
    RoiSdfCopyOverRequestedEvent,
} from '~/domain/events';
import { environmentState } from '~/state/atoms/engineAtoms';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import type { StructureManagementDeps } from './toolRunner';
import { useLiveStructureSet } from './useLiveStructureSet';

export function useStructureManagementDeps(): () => Omit<
    StructureManagementDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const setSelectedRoi = useSetRecoilState(selectedROIState);
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);
    const liveSet = useLiveStructureSet();

    return useCallback(() => {
        const uid = () => {
            const u = structureSet?.dicomUid;
            if (!u) {
                throw new Error('no structure set is loaded');
            }
            return u;
        };
        return {
            structures: projectStructureFacts(structureSet, env),
            liveSet,
            currentContextId: () => env?.structureSet?.sopInstanceUID,
            volumeCm3: (n: number) => env?.getRoiVolumeCm3(n) ?? Number.NaN,
            editVersion: (n: number) => env?.getRoi(n)?.sdfVersion,
            rename: async (n, name) => {
                await domainEventBus.publish(
                    new RoiNameChangeRequestedEvent(n, name, 'user')
                );
                setHasUnsavedChanges(true);
            },
            deleteRoi: async (n) => {
                setSelectedRoi(-1);
                await domainEventBus.publish(
                    new RoiDeletionRequestedEvent(uid(), n, 'user')
                );
            },
            duplicate: async (n) => {
                await domainEventBus.publish(
                    new RoiCopyRequestedEvent(uid(), n, 'user')
                );
            },
            replaceContours: async (source, target) => {
                await domainEventBus.publish(
                    new RoiSdfCopyOverRequestedEvent(
                        uid(),
                        source,
                        target,
                        'user'
                    )
                );
            },
            setType: async (n, type) => {
                await domainEventBus.publish(
                    new RoiInterpretedTypeChangeRequestedEvent(
                        n,
                        type as RoiInterpretedType,
                        'user'
                    )
                );
                setHasUnsavedChanges(true);
            },
        };
    }, [env, structureSet, setSelectedRoi, setHasUnsavedChanges, liveSet]);
}
